import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../contexts/ThemeContext';
import { ChatSessionsCommandBar } from '../components/chat/ChatSessionsCommandBar';
import { ChatSessionCard } from '../components/chat/ChatSessionCard';
import { ChatSessionTable } from '../components/chat/ChatSessionTable';
import { useChatSessions } from '../hooks/useChatSessions';
import * as chatService from '../services/chatService';
import type { ChatSession } from '../services/chatService';
import { sessionRoute } from '../utils/sessionRoute';
import { NewConversationButton } from '../components/common/NewConversationButton';
import { FiMessageSquare } from 'react-icons/fi';

type ViewMode = 'grid' | 'table';
type SortKey = 'lastActivity' | 'messageCount' | 'created' | 'personaName';
type SortDirection = 'asc' | 'desc';

const Sessions: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortKey, setSortKey] = useState<SortKey>('lastActivity');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  
  const { data: sessions = [], isLoading, error } = useChatSessions();

  const deleteMutation = useMutation(
    async (sessionId: string) => {
      await chatService.deleteChatSession(sessionId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chatSessions']);
      }
    }
  );
  
  const handleDelete = (sessionId: string) => {
    if (window.confirm('Are you sure you want to delete this chat session?')) {
      deleteMutation.mutate(sessionId);
    }
  };
  
  const handleArchive = (sessionId: string) => {
    console.log('Archive session:', sessionId);
  };
  
  const handleSessionClick = (sessionId: string) => {
    const session = sessions.find((s: ChatSession) => s.id === sessionId);
    if (session) {
      navigate(sessionRoute(session));
    }
  };
  
  const handleSortKeyChange = (key: SortKey) => {
    setSortKey(key);
  };
  
  const handleToggleSortDir = () => {
    setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
  };
  
  const filteredAndSortedSessions = useMemo(() => {
    let filtered = [...sessions];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((session: ChatSession) => 
        session.persona_name.toLowerCase().includes(query) ||
        (session.last_message && session.last_message.toLowerCase().includes(query)) ||
        session.id.toLowerCase().includes(query)
      );
    }
    
    filtered.sort((a: ChatSession, b: ChatSession) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortKey) {
        case 'lastActivity':
          aValue = new Date(a.last_activity).getTime();
          bValue = new Date(b.last_activity).getTime();
          break;
        case 'messageCount':
          aValue = a.message_count;
          bValue = b.message_count;
          break;
        case 'created':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'personaName':
          aValue = a.persona_name.toLowerCase();
          bValue = b.persona_name.toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDir === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      
      return sortDir === 'asc' ? aValue - bValue : bValue - aValue;
    });
    
    return filtered;
  }, [sessions, searchQuery, sortKey, sortDir]);
  
  if (error) {
    return (
      <div className={`text-center py-10 ${darkMode ? 'bg-red-900 bg-opacity-20 text-red-300' : 'text-red-600'}`}>
        <p className="text-xl mb-4">Error loading chat sessions</p>
        <p className="text-sm">{String(error)}</p>
      </div>
    );
  }
  
  return (
    <div className={`w-full ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      <ChatSessionsCommandBar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        sortKey={sortKey}
        onSortKeyChange={handleSortKeyChange}
        sortDir={sortDir}
        onToggleSortDir={handleToggleSortDir}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        resultCount={filteredAndSortedSessions.length}
        totalCount={sessions.length}
      />
      
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredAndSortedSessions.length === 0 ? (
        <div className={`
          text-center py-16 rounded-xl border-2 border-dashed
          ${darkMode 
            ? 'bg-gray-800/50 border-gray-700 text-gray-400' 
            : 'bg-gray-100 border-gray-200 text-gray-500'
          }
        `}>
          <FiMessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold mb-2">
            {searchQuery ? 'No sessions match your search' : 'No chat sessions yet'}
          </h3>
          <p className="mb-6 max-w-md mx-auto">
            {searchQuery 
              ? 'Try adjusting your search terms or clear the filters to see all sessions.'
              : 'Start a new conversation with one of our AI personas to begin collaborating.'
            }
          </p>
          {searchQuery ? (
            <button
              onClick={() => setSearchQuery('')}
              className={`
                px-4 py-2 rounded-lg font-medium
                ${darkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                  : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
                }
              `}
            >
              Clear Search
            </button>
          ) : (
            <NewConversationButton variant="primary" />
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredAndSortedSessions.map((session: ChatSession) => (
            <ChatSessionCard
              key={session.id}
              session={session}
              onDelete={handleDelete}
              onArchive={handleArchive}
              onClick={handleSessionClick}
            />
          ))}
        </div>
      ) : (
        <ChatSessionTable
          sessions={filteredAndSortedSessions}
          onDelete={handleDelete}
          onArchive={handleArchive}
          isLoading={isLoading}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortKeyChange={handleSortKeyChange}
          onToggleSortDir={handleToggleSortDir}
        />
      )}
    </div>
  );
};

export default Sessions;
